import { useEffect, useRef, useMemo, lazy, Suspense } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, ArrowDown } from 'lucide-react';
import CSSParticles from './CSSParticles';
import { IS_MOBILE, PREFERS_REDUCED_MOTION } from '@/lib/device';

// Lazy load heavy components
const ParticleNetwork = lazy(() => import('./ParticleNetwork'));

interface HeroProps {
  isLoaded?: boolean;
}

// Social Proof nur aus Dingen, die ein Besucher selbst nachprüfen kann.
// Keine Sammelzahlen ("50+ Projekte", "98 % Zufriedenheit"), die sich nicht
// belegen lassen — ein B2B-Entscheider hakt so etwas als Marketing ab, und
// bei einer Agentur mit Gründungsjahr 2024 wirkt es schnell unglaubwürdig.
//
// - PUBLISHED_CASES: Anzahl der Einträge in `cases` (Cases.tsx). Nicht
//   importiert, weil Cases lazy geladen wird und sonst im Hero-Bundle landet —
//   beim Hinzufügen eines Case hier mitzählen.
const PUBLISHED_CASES = 9;
const proofPoints = [
  { label: `${PUBLISHED_CASES} Projekte live ansehen`, href: '#cases' },
  { label: 'Antwort in unter 24h', href: '#kontakt' },
];

const Hero = ({ isLoaded = false }: HeroProps) => {
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  // Check mobile immediately on first render to avoid mounting ParticleNetwork then unmounting
  const isMobile = useMemo(() => {
    return typeof window !== 'undefined' && window.innerWidth < 768;
  }, []);

  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const proofRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    // Skip GSAP animations on mobile to avoid DOM conflicts.
    // Bei "Bewegung reduzieren" ebenfalls ueberspringen: die Elemente sind
    // ohne Tween bereits sichtbar, es wird nur die Einblendung ausgelassen.
    if (IS_MOBILE || PREFERS_REDUCED_MOTION) return;

    // Only run animation when loaded and hasn't animated yet
    if (!isLoaded || hasAnimated.current) return;
    hasAnimated.current = true;

    let ctx: { revert: () => void } | null = null;

    import('gsap').then(({ default: gsap }) => {
      ctx = gsap.context(() => {
        /*
         * Timings deutlich gekürzt. Vorher: Titel 1,4 s mit 0,3 s Delay,
         * Subline 1,2 s mit 1,2 s Delay, CTAs 0,8 s mit 1,6 s Delay — der
         * primäre CTA erschien also erst nach 2,4 s. Das war kaschiert,
         * solange der Preloader vorher lief; ohne ihn steht der Besucher
         * vor einem Hero ohne Handlungsaufforderung.
         *
         * Neu: alles unter 700 ms komplett, Reihenfolge bleibt erhalten.
         */
        gsap.fromTo(
          titleRef.current,
          { opacity: 0, y: 24 },
          { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out', delay: 0.05 }
        );

        gsap.fromTo(
          subtitleRef.current,
          { opacity: 0, y: 16 },
          { opacity: 1, y: 0, duration: 0.4, delay: 0.18, ease: 'power3.out' }
        );

        gsap.fromTo(
          ctaRef.current?.children || [],
          { opacity: 0, y: 12 },
          { opacity: 1, y: 0, duration: 0.35, stagger: 0.06, delay: 0.3, ease: 'power3.out' }
        );
      });
    });

    return () => { ctx?.revert(); };
  }, [isLoaded]);

  const scrollToSection = (id: string) => {
    const element = document.querySelector(id);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      ref={sectionRef}
      /*
       * Höhe: mobil bewusst NICHT die volle Viewporthöhe. Bei 100svh und
       * vertikaler Zentrierung stand der Eyebrow gemessen 263px unter der
       * Oberkante (390x844) — die Navbar endet bei 124px, dazwischen lag also
       * ein leeres Feld von 139px. 78svh zentriert setzt ihn auf ~150px und
       * schiebt gleichzeitig die nächste Sektion in den ersten Bildschirm,
       * statt unter dem CTA ein totes Loch zu lassen.
       * Ab md volle Höhe minus StatusBar, damit der Scroll-Indikator im Bild
       * bleibt; dort ist die Höhe kein Engpass.
       */
      className="relative flex items-center justify-center overflow-hidden min-h-[78svh] md:min-h-[calc(100vh_-_var(--statusbar-h,_0px))]"
    >
      {/* Particle Background - Canvas on desktop, CSS on mobile */}
      {isMobile ? <CSSParticles /> : (
        <Suspense fallback={null}>
          <ParticleNetwork />
        </Suspense>
      )}

      {/* Subtle gradient overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/40 to-background/70 pointer-events-none z-[1]" />

      {/*
       * Content. Ab md vorher pt-0: Solange der Inhalt kürzer als der
       * Viewport war, hat die vertikale Zentrierung den Abstand zur fixierten
       * Navigation geliefert. Auf niedrigen Fenstern (Laptop mit kleinem
       * Browserfenster, ~550px Höhe) ist der Inhalt höher als die Sektion,
       * beginnt dann bei y=0 und die H1 lag unter StatusBar + Navbar
       * (44 + 80 px). Jetzt reserviert das Padding genau diese Höhe; unten
       * Platz für den Scroll-Indikator.
       */}
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-12 text-center pt-[calc(var(--statusbar-h,0px)+5.5rem)] pb-12 md:pt-[calc(var(--statusbar-h,0px)+6rem)] md:pb-28">
        {/* Eyebrow text */}
        <div className="overflow-hidden mb-4 md:mb-6">
          <p className="text-[13px] sm:text-sm md:text-base text-primary uppercase tracking-[0.2em] sm:tracking-[0.3em] font-medium animate-fade-in">
            Digitalagentur aus Ludwigsburg
          </p>
        </div>

        {/*
         * EINE Headline für alle Breiten.
         *
         * Vorher standen hier zwei Fassungen nebeneinander (`md:hidden` /
         * `hidden md:inline`): Mobil und Desktop versprachen Unterschiedliches
         * und jede Copy-Änderung musste doppelt gepflegt werden. Beide
         * Fassungen sprachen außerdem nur Shop-Betreiber an — Web-Anwendungen,
         * CRM-Systeme und Automatisierung fielen aus der Ansprache raus.
         *
         * "Mehr Umsatz. Weniger Handarbeit." benennt beide Ergebnisse, die
         * beyerdigital verkauft, ohne sich auf eine Kanal-Nische zu verengen,
         * und ist kurz genug, dass sie auf 390px wie auf 2560px in zwei
         * Zeilen steht — kein manueller <br> nötig, `text-balance` verteilt.
         *
         * Größe weiterhin clamp(30px, 9vw, 44px) mobil statt der alten fixen
         * 30px; die Leistungs-Keywords tragen jetzt die Subline direkt darunter.
         */}
        {/*
         * Betonung über Farbe statt über einen Verlauf.
         *
         * Vorher lief hier ein animierter Gradient (weiß → grün → gold → grün
         * → weiß, 8-Sekunden-Endlosschleife) über die gesamte Headline. Das
         * zieht dauerhaft Aufmerksamkeit auf die Bewegung statt auf die
         * Aussage, und die Buchstaben wechseln dabei ihren Kontrast gegen den
         * schwarzen Grund. Jetzt trägt das Versprechen Weiß, der konkrete
         * Nutzen das Markengrün — die Hierarchie bleibt, die Unruhe geht.
         */}
        <h1
          ref={titleRef}
          className="mb-6 md:mb-8 mx-auto max-w-[16ch] md:max-w-[20ch] text-[clamp(1.875rem,9vw,2.75rem)] md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl text-balance font-bold leading-[1.05] md:leading-[0.95] tracking-[-0.02em] md:tracking-[-0.03em] px-2"
        >
          Mehr Umsatz. <span className="text-primary">Weniger Handarbeit.</span>
        </h1>

        {/*
         * Ebenfalls eine Fassung statt zwei. Die Subline trägt die
         * Leistungs-Keywords, die aus der H1 rausgefallen sind, und deckt
         * sich mit der Meta-Description der Seite: Shopify, Web-Anwendungen,
         * KI-Automatisierung, Mittelstand.
         */}
        <p
          ref={subtitleRef}
          className="text-base sm:text-lg md:text-xl lg:text-2xl text-muted-foreground max-w-[38ch] md:max-w-3xl mx-auto mb-8 md:mb-12 text-balance px-4"
        >
          Shopify-Shops, individuelle Web-Anwendungen und KI-Automatisierung für
          den Mittelstand — gebaut auf Ergebnisse, nicht auf Effekte.
        </p>

        {/*
         * Hierarchie: EIN primärer CTA. Der sekundäre Weg war vorher ein
         * gleich großer Outline-Button daneben — zwei gleich laute Buttons,
         * also keiner laut. Jetzt Textlink mit Unterstrich.
         */}
        <div ref={ctaRef} className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center px-4">
          <Button
            size="lg"
            onClick={() => scrollToSection('#kontakt')}
            className="magnetic w-full sm:w-auto text-base md:text-lg px-8 py-6 glow-accent hover:scale-105 transition-transform"
          >
            Projekt anfragen
          </Button>
          <button
            type="button"
            onClick={() => scrollToSection('#cases')}
            className="magnetic group inline-flex items-center gap-2 min-h-11 px-2 text-base md:text-lg text-muted-foreground underline underline-offset-4 decoration-muted-foreground/40 hover:text-foreground hover:decoration-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm transition-colors"
          >
            Cases ansehen
            {/* Pfeil statt Play: das Play-Dreieck versprach ein Video, dahinter
                liegen aber Case-Studies. Icon nach dem Label, weil es die
                Richtung anzeigt und nicht den Typ der Aktion. */}
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
          </button>
        </div>

        {/* Nachprüfbare Fakten als Links. Keine Sterne/Bewertungen ohne echte,
            überprüfbare Quelle (Google-Profil o. Ä.). */}
        <div ref={proofRef} className="mt-8 flex flex-col items-center gap-3 px-4">
          <ul
            aria-label="Fakten"
            className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs sm:text-sm text-muted-foreground"
          >
            {proofPoints.map((point, index) => (
              <li key={point.label} className="flex items-center gap-3">
                {index > 0 && <span className="text-white/30" aria-hidden="true">•</span>}
                <a
                  href={point.href}
                  onClick={(e) => { e.preventDefault(); scrollToSection(point.href); }}
                  className="min-h-8 inline-flex items-center rounded-sm underline underline-offset-4 decoration-white/20 hover:text-foreground hover:decoration-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-colors"
                >
                  {point.label}
                </a>
              </li>
            ))}
            <li className="hidden md:flex items-center gap-3">
              <span className="text-white/30" aria-hidden="true">•</span>
              <span className="text-primary font-medium">Ludwigsburg & Stuttgart</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Animated scroll indicator — mobil ausgeblendet, dort lag er über den Proof-Links */}
      <div className="absolute bottom-6 md:bottom-12 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 md:gap-3">
        <div className="w-5 h-8 md:w-6 md:h-10 border-2 border-muted-foreground/50 rounded-full flex justify-center p-1.5 md:p-2">
          <div className="w-1 h-1.5 md:h-2 bg-primary rounded-full animate-scroll-bounce" />
        </div>
        <ArrowDown className="w-3 h-3 md:w-4 md:h-4 text-muted-foreground animate-scroll-hint" />
      </div>
    </section>
  );
};

export default Hero;
